import { Autocomplete, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { selectorFamily, useRecoilValue } from "recoil";
import { userState, } from "../atoms";
import { CodiceDescrizione } from "../models";
import { NullableString, ServiceDescriptor } from "../models/ServiceDescriptor";
import apiService from "../services/api";
import { MySixtemaDialogTitle } from "./ContextSelectorDialog";

export const codiciState = selectorFamily({
    key: 'codiciState',
    get: ({ appl, uffi }: { appl: NullableString | undefined, uffi?: string }) => ({ get }) => {
        const userCtx = get(userState)?.context
        if (!userCtx) return
        if (!appl) return;
        // console.log(`Computing codiciState: ${appl} ${uffi ?? "NULL"}`)
        const token = userCtx.authorizationToken
        return apiService.getCodici(appl, token)
            .then(data => data?.map<CodiceDescrizione>(x => ({ ...x, value: x.codice, label: `${x.codice} - ${x.descrizione}` })))
    }
})

type CodiciModalPropsType = {
    open: boolean,
    appl?: ServiceDescriptor,
    uffi?: string,
    invite?: string,
    label?: string,
    onHide?: () => void,
    onSelected: (sd: ServiceDescriptor, codice: string) => void,
}

const cdDefault: CodiceDescrizione = { label: '', value: '', codice: '', descrizione: '' }
const CodiciModal = ({ open, onHide, appl, uffi, invite, label, onSelected }: CodiciModalPropsType) => {
    const codici = useRecoilValue(codiciState({ appl: appl?.guid, uffi }))
    const [codiceSelected, setCodiceSelected] = useState<CodiceDescrizione>(cdDefault);

    const internalHide = useCallback(() => {
        setCodiceSelected(cdDefault)
        onHide && onHide()
    }, [onHide])

    useEffect(() => {
        if (codici && codici.length === 1) {
            appl && onSelected(appl, codici[0].value)
            internalHide()
        }
    }, [appl, codici, internalHide, onSelected])

    const handleClose = (event: {}, reason?: "backdropClick" | "escapeKeyDown") => {
        if (reason && reason === "backdropClick") return;
        internalHide()
    }
    const handleConfirm = () => {
        appl && codiceSelected && codiceSelected.value && onSelected(appl, codiceSelected.value)
        internalHide()
    }

    const handleOnSelect = (codice: CodiceDescrizione) => {
        setCodiceSelected(codice)
    }

    return (
        <Dialog onClose={handleClose} open={open} maxWidth='xs' fullWidth>
            <MySixtemaDialogTitle onClose={onHide}>
                <Typography><span>{appl?.urlDescription}</span></Typography>
            </MySixtemaDialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
                <div className="fs-5 mb-3">{invite ?? 'Selezione codice'}</div>
                <CodiciSelector appl={appl} uffi={uffi} codiceSelected={codiceSelected} onSelect={handleOnSelect} variant="full" />
                <div className="fs-6 mt-3">{label ?? 'Codice selezionato'}: <b>{codiceSelected && <span>{codiceSelected.value}</span>}</b></div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onHide}>Annulla</Button>
                {codiceSelected && <Button onClick={handleConfirm}>Conferma</Button>}
            </DialogActions>
        </Dialog>
    )
}
export default CodiciModal

export type CodiciSelectorPropsType = {
    appl?: ServiceDescriptor,
    uffi?: string,
    codiceSelected?: CodiceDescrizione,
    onSelect: (codice: CodiceDescrizione) => void,
    variant: string,
    fullWidth?: boolean,
}

export const CodiciSelector: React.FC<CodiciSelectorPropsType> = ({ appl, uffi, codiceSelected, variant, fullWidth, onSelect }) => {
    const codici = useRecoilValue(codiciState({ appl: appl?.guid, uffi }))
    const handleOnChange = (option: CodiceDescrizione) => onSelect(option)
    const getDescrizione = (o: CodiceDescrizione) => {
        switch (variant) {
            case 'full': return `${o.codice} - ${o.descrizione}`
            case 'codice': return o.codice
            case 'descrizione':
            default: return o.descrizione
        }
    }
    useEffect(() => {
        if(codiceSelected) {
            const cs = codici?.find(x => x.codice === codiceSelected.codice)
            if(cs && cs !== codiceSelected) onSelect(cs)
        }
    }, [codiceSelected, codici, onSelect])

    return (
        <>
            {codici &&
                <Autocomplete id={`ctx-codice-autocomplete`} sx={{ my: 0.5, width: fullWidth ? '100%' : undefined }}
                    getOptionLabel={getDescrizione}
                    options={codici}
                    value={codiceSelected}
                    onChange={(event: React.SyntheticEvent<Element, Event>, newValue: CodiceDescrizione | null) => {
                        if (newValue)
                            handleOnChange(newValue)
                    }}
                    renderInput={(params) => (<TextField {...params} label={codiceSelected?.label} variant="standard" />)}
                />
            }
        </>
    )

}